// src/views/Billing/Invoices/InvoiceModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { activityActions } from "../../redux/actions";

const DEFAULT_VALUES = {
  currency: "PKR",
  amount: "",
  vat: 0, // %
  // Manual fields
  companyName: "",
  companyAddress: "",
  candidateName: "",
  positionName: "",
  // System fields
  projectId: "",
  acceptedActivityId: "",
};

const currencyOptions = ["PKR", "USD", "EUR", "SAR", "AED", "GBP"];

export default function InvoiceModal({ show, onHide, mode, setShowInvoice }) {
  const dispatch = useDispatch();
  const isAdd = mode === "ADD";

  const initialData =
    useSelector((s) => s.activityReducer?.invoiceData) || null;

  // Expected: array of accepted activities like in your JSON
  const acceptedActivity =
    useSelector((s) => s.activityReducer?.acceptedActivity) || [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES, shouldUnregister: true });

  // Toggle: 'system' | 'manual'
  const [generationMode, setGenerationMode] = useState("system");

  // Derived lists from acceptedActivity
  const projects = useMemo(() => {
    if (!Array.isArray(acceptedActivity)) return [];
    // unique by projectId
    const map = new Map();
    acceptedActivity.forEach((a) => {
      if (!map.has(a.projectId)) {
        map.set(a.projectId, {
          value: a.projectId,
          label: a?.project?.positionName || `Project #${a.projectId}`,
        });
      }
    });
    return Array.from(map.values());
  }, [acceptedActivity]);

  const selectedProjectId = watch("projectId");
  const candidateOptions = useMemo(() => {
    if (!Array.isArray(acceptedActivity) || !selectedProjectId) return [];
    const pid = Number(selectedProjectId);
    return acceptedActivity
      .filter((a) => a.projectId === pid)
      .map((a) => ({
        value: a.id, // IMPORTANT: this is the acceptedActivity.id we’ll submit
        label:
          a?.user?.name || a?.user?.email || `Candidate #${a?.userId ?? a.id}`,
      }));
  }, [acceptedActivity, selectedProjectId]);

  // Live totals
  const amount = watch("amount");
  const vat = watch("vat");
  const total = useMemo(() => {
    const a = parseFloat(amount || 0) || 0;
    const v = parseFloat(vat || 0) || 0;
    return (a + (a * v) / 100).toFixed(2);
  }, [amount, vat]);

  // Prefill on open
  useEffect(() => {
    if (!show) return;

    setGenerationMode("system");
    reset(DEFAULT_VALUES);

    // preselect when only one project
    if (projects.length === 1) {
      setValue("projectId", String(projects[0].value));
    }
  }, [show, reset, projects, setValue]);

  // When project changes, clear candidate
  useEffect(() => {
    setValue("acceptedActivityId", "");
  }, [selectedProjectId, setValue]);

  const onSubmit = (values) => {
    const a = parseFloat(values.amount || 0) || 0;
    const v = parseFloat(values.vat || 0) || 0;
    const acceptedActivityId =
      generationMode === "system" ? Number(values.acceptedActivityId) : 0;
    const payload = {
      id: acceptedActivityId,
      activityId: acceptedActivityId, // keep if your API expects it (system path may ignore)
      mode: generationMode, // 'system' or 'manual'
      currency: values.currency,
      amount: a,
      vat: v,
      total: parseFloat((a + (a * v) / 100).toFixed(2)),
      ...(generationMode === "manual" && {
        companyName: values.companyName?.trim(),
        companyAddress: values.companyAddress?.trim(),
        candidateName: values.candidateName?.trim(),
        position: values.positionName?.trim(),
      }),
      ...(generationMode === "system" && {
        projectId: Number(values.projectId),
      }),
    };

    dispatch(activityActions.generateInvoice(payload, 0, setShowInvoice));
    onHide?.();
  };

  const title = "Add Invoice";

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1: Choose generation mode */}
          <fieldset>
            <div className="mb-3">
              <label className="form-label d-block">
                How do you want to generate?
              </label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="gen-system"
                    name="generationMode"
                    checked={generationMode === "system"}
                    onChange={() => setGenerationMode("system")}
                  />
                  <label className="form-check-label" htmlFor="gen-system">
                    System Generate
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="gen-manual"
                    name="generationMode"
                    checked={generationMode === "manual"}
                    onChange={() => setGenerationMode("manual")}
                  />
                  <label className="form-check-label" htmlFor="gen-manual">
                    Manual
                  </label>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            {/* SYSTEM: Project & Candidate (from acceptedActivity) */}
            {generationMode === "system" && (
              <div className="row g-3 mb-1">
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <select
                      id="project"
                      className="form-control"
                      {...register("projectId", {
                        validate: (v) =>
                          generationMode === "system"
                            ? !!v || "Project is required"
                            : true,
                      })}
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="project">Project</label>
                  </div>
                  {errors.projectId && (
                    <small className="text-danger">
                      {errors.projectId.message}
                    </small>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <select
                      id="candidate"
                      className="form-control"
                      disabled={!selectedProjectId}
                      {...register("acceptedActivityId", {
                        validate: (v) =>
                          generationMode === "system"
                            ? !!v || "Candidate is required"
                            : true,
                      })}
                    >
                      <option value="">
                        {selectedProjectId
                          ? "Select candidate"
                          : "Select a project first"}
                      </option>
                      {candidateOptions.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="candidate" className="form-label">
                      Candidate
                    </label>
                  </div>
                  {errors.acceptedActivityId && (
                    <small className="text-danger">
                      {errors.acceptedActivityId.message}
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* MANUAL: company/candidate/position text fields */}
            {generationMode === "manual" && (
              <div className="row g-3 mb-1">
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      id="company"
                      type="text"
                      className="form-control"
                      {...register("companyName", {
                        validate: (v) =>
                          generationMode === "manual"
                            ? !!v?.trim() || "Company name is required"
                            : true,
                      })}
                    />
                    <label htmlFor="company">Company Name</label>
                  </div>
                  {errors.companyName && (
                    <small className="text-danger">
                      {errors.companyName.message}
                    </small>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      id="address"
                      type="text"
                      className="form-control"
                      {...register("companyAddress", {
                        validate: (v) =>
                          generationMode === "manual"
                            ? !!v?.trim() || "Company address is required"
                            : true,
                      })}
                    />
                    <label htmlFor="address" className="form-label">
                      Company Address
                    </label>
                  </div>
                  {errors.companyAddress && (
                    <small className="text-danger">
                      {errors.companyAddress.message}
                    </small>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      id="canName"
                      type="text"
                      className="form-control"
                      {...register("candidateName", {
                        validate: (v) =>
                          generationMode === "manual"
                            ? !!v?.trim() || "Candidate name is required"
                            : true,
                      })}
                    />
                    <label htmlFor="canName" className="form-label">
                      Candidate Name
                    </label>
                  </div>
                  {errors.candidateName && (
                    <small className="text-danger">
                      {errors.candidateName.message}
                    </small>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      id="position"
                      type="text"
                      className="form-control"
                      {...register("positionName", {
                        validate: (v) =>
                          generationMode === "manual"
                            ? !!v?.trim() || "Position name is required"
                            : true,
                      })}
                    />
                    <label htmlFor="postion" className="form-label">
                      Position Name
                    </label>
                  </div>
                  {errors.positionName && (
                    <small className="text-danger">
                      {errors.positionName.message}
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Shared fields */}
            <div className="row g-3">
              <div className="col-12 col-md-6 mt-4">
                <div className="form-floating">
                  <select
                    id="currency"
                    className="form-control"
                    {...register("currency", {
                      required: "Currency is required",
                    })}
                  >
                    {currencyOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                </div>
                {errors.currency && (
                  <small className="text-danger">
                    {errors.currency.message}
                  </small>
                )}
              </div>

              <div className="col-12 col-md-6 mt-4">
                <div className="form-floating">
                  <input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    placeholder="0.00"
                    {...register("amount", {
                      required: "Amount is required",
                      validate: (v) =>
                        (parseFloat(v) || 0) > 0 ||
                        "Amount must be greater than 0",
                    })}
                  />
                  <label htmlFor="amount" className="form-label">
                    Amount
                  </label>
                </div>
                {errors.amount && (
                  <small className="text-danger">{errors.amount.message}</small>
                )}
              </div>

              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="vat"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="form-control"
                    {...register("vat", {
                      required: "VAT is required",
                      min: { value: 0, message: "VAT cannot be negative" },
                      max: { value: 100, message: "VAT cannot exceed 100" },
                    })}
                  />
                  <label htmlFor="vat" className="form-label">
                    VAT (%)
                  </label>
                </div>
                {errors.vat && (
                  <small className="text-danger">{errors.vat.message}</small>
                )}
              </div>

              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="total"
                    type="text"
                    className="form-control"
                    value={`${watch("currency") || "PKR"} ${total}`}
                    readOnly
                  />
                  <label htmlFor="total" className="form-label">
                    Total (Amount + VAT)
                  </label>
                </div>
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 footer-modal">
            <Button type="button" variant="outline-secondary" onClick={onHide} className="btn-modal">
              Cancel
            </Button>
            <Button type="submit" variant="success" className="btn-modal">
              Generate Invoice
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
